import { useAxios } from '@/context/axiosContext'
import { useDialog } from '@/context/dialogContext'
import {
  FeedbackListData,
  formatDateString,
  PraiseIcon,
  smartLineBreak,
  SuggestionIcon,
} from '@/pages/feedback/feedbackItem'
import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import GGOOLLFF from '@/assets/images/thumbnaillogo500px.png'
import { formatDate } from '@/pages/promotion/promotionItem'
import { BoardTitle, MainBoardWrapper } from '../board/mainBoard'
import './mainFeedback.css'
import styled from 'styled-components'
import { useResponsive } from '@/hooks/useResponsive'
import { Suggestion } from '@/pages/feedback/_components/suggestion'
import { Praise } from '@/pages/feedback/_components/praise'
import LikeButton from '@/pages/feedback/_components/likeButton'

type TInput = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement

const PraiseNoResult = () => (
  <div className="mainFeedback-no-result type-01">
    <div>
      <img src={GGOOLLFF} alt="" width={'100px'} />
    </div>
    <div style={{ fontSize: '10px', textAlign: 'center' }}>
      아직 등록된 내용이 없거나,
      <br />
      찾으시는 결과가 없어요.
    </div>
  </div>
)

const SuggestionNoResult = () => (
  <div className="mainFeedback-no-result type-02">
    <div>
      <img src={GGOOLLFF} alt="" width={'100px'} />
    </div>
    <div style={{ fontSize: '10px', textAlign: 'center' }}>
      아직 등록된 내용이 없거나,
      <br />
      찾으시는 결과가 없어요.
    </div>
  </div>
)

export const MainFeedback = () => {
  const axios = useAxios()
  const location = useLocation()
  const navigate = useNavigate()
  const { showAlert } = useDialog()
  const { isMobile, isTablet, isDesktop } = useResponsive()

  // 스크롤 위치 참조를 위한 ref 추가
  const containerRef = useRef<HTMLDivElement>(null)

  // 검색 관련 상태 - 실제 검색에 적용될 값들
  const [appliedSearchParams, setAppliedSearchParams] = useState<{
    keyword: string
    keywordTypes: string[]
    rndDt?: string
  }>({
    keyword: '',
    keywordTypes: ['nttSj'],
    rndDt: '',
  })

  // UI 상태 - 사용자가 입력 중인 값들
  const [uiState, setUiState] = useState<{
    keyword: string
    keywordTypes: string[]
    rndDt?: string
  }>({
    keyword: '',
    keywordTypes: ['nttSj'],
    rndDt: '',
  })

  // 정렬 타입 (즉시 적용)
  const [sortType, setSortType] = useState('recent')

  // 각각의 페이지 상태
  const [praisePage, setPraisePage] = useState(1)
  const [suggestionPage, setSuggestionPage] = useState(1)
  // 데이터 상태
  const [praiseList, setPraiseList] = useState<FeedbackListData[]>([])
  const [suggestionList, setSuggestionList] = useState<FeedbackListData[]>([])
  const [praiseCnt, setPraiseCnt] = useState(0)
  const [suggestionCnt, setSuggestionCnt] = useState(0)

  // // 스크롤업 함수
  // const scrollToTop = () => {
  //   // feedback-container로 스크롤하거나, 페이지 최상단으로 스크롤
  //   if (containerRef.current) {
  //     containerRef.current.scrollIntoView({
  //       behavior: 'smooth',
  //       block: 'start',
  //     })
  //   } else {
  //     window.scrollTo({
  //       top: 0,
  //       behavior: 'smooth',
  //     })
  //   }
  // }

  // 정렬 타입이 변경되거나 실제 검색 조건이 변경되면 양쪽 데이터 모두 새로 불러오기
  useEffect(() => {
    if (hasSearchConditions()) {
      loadBothLists()
    }
  }, [sortType, appliedSearchParams])

  // 개별 페이지 변경 시에만 해당 리스트만 업데이트
  useEffect(() => {
    if (hasSearchConditions()) {
      getPraiseList()
      // 페이지 변경 시 스크롤업
    }
  }, [praisePage])

  useEffect(() => {
    if (hasSearchConditions()) {
      getSuggestionList()
      // 페이지 변경 시 스크롤업
    }
  }, [suggestionPage])

  const hasSearchConditions = () => {
    return (
      appliedSearchParams.keywordTypes.length > 0 ||
      appliedSearchParams.keyword.trim() !== ''
    )
  }

  const buildSearchParams = (page: number) => {
    const params: any = { page }

    if (sortType) params.sortType = sortType
    if (appliedSearchParams.keywordTypes.length > 0) {
      params.keywordTypes = appliedSearchParams.keywordTypes.join(',')
    }
    if (appliedSearchParams.keyword.trim())
      params.keyword = appliedSearchParams.keyword
    if (
      appliedSearchParams.keywordTypes.includes('rndDt') &&
      appliedSearchParams.rndDt
    ) {
      params.rndDt = appliedSearchParams.rndDt.replaceAll('-', '')
    }

    return params
  }

  // 검색 조건 적용 시 양쪽 모두 첫 페이지로 리셋하여 로드
  const loadBothLists = async () => {
    setPraisePage(1)
    setSuggestionPage(1)
    await Promise.all([getPraiseList(1), getSuggestionList(1)])
  }

  // 칭찬 리스트만 로드
  const getPraiseList = async (page = praisePage) => {
    try {
      const params = buildSearchParams(page)
      const response = await axios.get('/feedback/getPraiseList', {
        headers: { 'Cache-Control': 'no-cache' },
        params,
      })

      const { praiseList, praiseCount } = response.data.data
      setPraiseList(praiseList || [])
      setPraiseCnt(praiseCount || 0)
    } catch (err) {
      console.error('Error fetching praise list:', err)
      setPraiseList([])
      setPraiseCnt(0)
    }
  }

  // 제안 리스트만 로드
  const getSuggestionList = async (page = suggestionPage) => {
    try {
      const params = buildSearchParams(page)
      const response = await axios.get('/feedback/getSuggestionList', {
        headers: { 'Cache-Control': 'no-cache' },
        params,
      })

      const { suggestionList, suggestionCount } = response.data.data
      setSuggestionList(suggestionList || [])
      setSuggestionCnt(suggestionCount || 0)
    } catch (err) {
      console.error('Error fetching suggestion list:', err)
      setSuggestionList([])
      setSuggestionCnt(0)
    }
  }

  const handleDetailView = (nttSn: number) => {
    navigate(`/feedback/detail?nttSn=${nttSn}`)
  }

  // 정렬 타입 변경 (즉시 적용)
  const handleSortTypeChange = (value: string) => {
    setSortType(value)
  }

  // UI 상태 변경 (검색 버튼 클릭 전까지는 실제 검색에 적용되지 않음)
  const handleKeywordTypesChange = (value: string) => {
    setUiState((prev) => {
      const isChecked = prev.keywordTypes.includes(value)
      return {
        ...prev,
        keywordTypes: isChecked
          ? prev.keywordTypes.filter((v) => v !== value)
          : [...prev.keywordTypes, value],
      }
    })
  }

  // 검색 버튼 클릭 시 UI 상태를 실제 검색 파라미터에 적용
  const handleSearchClick = () => {
    if (uiState.keywordTypes.length === 0) {
      showAlert({
        title: '검색 조건을 하나 이상 선택해주세요.',
      })
      return
    }

    // UI 상태를 실제 검색 파라미터에 적용
    setAppliedSearchParams({
      keyword: uiState.keyword,
      keywordTypes: uiState.keywordTypes,
      rndDt: uiState.rndDt,
    })
  }

  const handleInputKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearchClick()
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUiState((prev) => ({
      ...prev,
      keyword: e.target.value,
    }))
  }

  const handleDateChange = (e: React.ChangeEvent<TInput>) => {
    const { value } = e.target

    if (new Date(value) > new Date()) {
      showAlert({
        type: 'error',
        title: '오늘 이전 날짜만 선택할 수 있어요.',
      })
      return
    }

    setUiState((prev) => ({
      ...prev,
      rndDt: value,
    }))
  }

  // 현재 페이지에서 실제 표시되는 아이템 수 계산
  const getDisplayedItemCount = (
    totalCount: number,
    currentPage: number,
    itemsPerPage = 10,
  ) => {
    const startIndex = (currentPage - 1) * itemsPerPage
    const remainingItems = totalCount - startIndex
    return Math.min(remainingItems, itemsPerPage)
  }

  // 두 리스트 중 더 많은 아이템을 표시하는 쪽의 아이템 수 계산
  const getMaxDisplayedItems = () => {
    const praiseDisplayed = getDisplayedItemCount(praiseCnt, praisePage)
    const suggestionDisplayed = getDisplayedItemCount(
      suggestionCnt,
      suggestionPage,
    )
    return Math.max(praiseDisplayed, suggestionDisplayed)
  }

  // Praise와 Suggestion 컴포넌트에 전달할 props에 추가
  const maxItems = getMaxDisplayedItems()

  const moveFeedback = () => {
    window.scrollTo(0, 0)
    navigate(`/feedback`)
  }
  const [activeTab, setActiveTab] = useState('good')
  // 탭 클릭 핸들러
  const handleTabClick = (tabType: string) => {
    setActiveTab(tabType)
  }
  return (
    <>
      <MainFeedbackWrapper
        style={{ cursor: 'pointer', display: 'flex', flexDirection: 'column' }}
      >
        <BoardTitle onClick={moveFeedback}>
          <div>{'목소리'}</div>
          <div
            style={{
              fontSize: '13px',
              color: '#888888',
              fontWeight: 'normal',
            }}
          >
            {'더보기'}
          </div>
        </BoardTitle>

        {!isMobile ? (
          <div className="mainfeedback-container" ref={containerRef}>
            <div className="mainfeedback-section">
              <div className="mainfeedback-title">
                <span>꿀소리</span>
                <PraiseIcon width={'20px'} height={'20px'} />
              </div>
              <div className="mainfeedback-title-divider border-green" />
              {praiseList.length > 0 ? (
                <>
                  <div className="mainfeedback-list-container">
                    {praiseList.slice(0, 3).map((item, index) => (
                      <div
                        key={item.nttSn}
                        className="mainfeedback-praise-item"
                      >
                        <div
                          className="mainfeedback-centered-container"
                          onClick={() => handleDetailView(item.nttSn)}
                        >
                          <div className="mainfeedback-centered-text">
                            {smartLineBreak(item.bizNm)}
                          </div>
                        </div>
                        <div className="mainfeedback-praise-divider" />
                        <div className="mainfeedback-meta">
                          <div className="mainfeedback-info">
                            <div
                              className="mainfeedback-meta-text"
                              title={item.nttSj}
                            >
                              <span className="mainfeedback-nttSj">
                                {item.nttSj}
                              </span>
                              {item.answerCnt ? (
                                <span className="mainfeedback-answer-count">
                                  [{item.answerCnt}]
                                </span>
                              ) : null}
                            </div>
                            <div className="mainfeedback-meta-subtext">
                              {!isMobile && (
                                <>
                                  <span
                                    className="mainfeedback-nickname"
                                    title={item.nickName}
                                  >
                                    {item.nickName}
                                  </span>
                                  {` | `}
                                </>
                              )}
                              {` ${formatDate(item.regDate, true)} | 조회 : ${item.readngCnt}`}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <PraiseNoResult />
              )}
            </div>
            <div className="divider" />
            <div className="mainfeedback-section">
              <div className="mainfeedback-title">
                <span>쓴소리</span>
                <SuggestionIcon width={'20px'} height={'20px'} />
              </div>
              <div className="mainfeedback-title-divider border-red" />

              {suggestionList.length > 0 ? (
                <>
                  <div className="mainfeedback-list-container">
                    {suggestionList.slice(0, 3).map((item, index) => (
                      <div
                        key={item.nttSn}
                        className="mainfeedback-suggestion-item"
                      >
                        <div
                          className="mainfeedback-centered-container"
                          onClick={() => handleDetailView(item.nttSn)}
                        >
                          <div className="mainfeedback-centered-text">
                            {smartLineBreak(item.bizNm)}
                          </div>
                        </div>
                        <div className="mainfeedback-suggestion-divider" />
                        <div className="mainfeedback-meta">
                          <div className="mainfeedback-info">
                            <div
                              className="mainfeedback-meta-text"
                              title={item.nttSj}
                            >
                              <span className="mainfeedback-nttSj">
                                {item.nttSj}
                              </span>
                              {item.answerCnt ? (
                                <span className="mainfeedback-answer-count">
                                  [{item.answerCnt}]
                                </span>
                              ) : null}
                            </div>
                            <div className="mainfeedback-meta-subtext">
                              {!isMobile && (
                                <>
                                  <span
                                    className="mainfeedback-nickname"
                                    title={item.nickName}
                                  >
                                    {item.nickName}
                                  </span>
                                  {` | `}
                                </>
                              )}
                              {` ${formatDate(item.regDate, true)} | 조회 : ${item.readngCnt}`}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <SuggestionNoResult />
              )}
            </div>
          </div>
        ) : (
          <>
            <div
              className="mobile-fb-container"
              style={{ margin: '0px', padding: '0px' }}
            >
              <div
                className="mobile-fb-mobile-tabs"
                style={{ marginBottom: '10px', marginTop: '10px' }}
              >
                <button
                  className={`mobile-fb-tab-btn praise ${activeTab === 'good' ? 'active' : ''}`}
                  data-tab="good"
                  onClick={() => handleTabClick('good')}
                >
                  꿀소리
                </button>
                <button
                  className={`mobile-fb-tab-btn suggestion ${activeTab === 'bad' ? 'active' : ''}`}
                  data-tab="bad"
                  onClick={() => handleTabClick('bad')}
                >
                  쓴소리
                </button>
              </div>
            </div>
            <div ref={containerRef}>
              {activeTab === 'good' && (
                <>
                  <section
                    className={`mobile-fb-reviews-section good-reviews ${activeTab === 'good' ? 'active' : ''}`}
                    id="good-reviews"
                    style={{ margin: '0px', padding: '0px' }}
                  >
                    <div className="mobile-fb-reviews-grid">
                      {praiseList.slice(0, 3).map((item, index) => (
                        <div className="mobile-fb-review-card" key={item.nttSn}>
                          <h3
                            className="mobile-fb-book-title"
                            onClick={() => handleDetailView(item.nttSn)}
                          >
                            {smartLineBreak(item.bizNm)}
                          </h3>
                          <div
                            className="mobile-fb-review-meta"
                            onClick={() => handleDetailView(item.nttSn)}
                          >
                            <span
                              className="mobile-fb-review-date"
                              onClick={() => handleDetailView(item.nttSn)}
                            >
                              {`라운드날짜 : ${formatDateString(item.rndDt)}`}
                            </span>
                          </div>
                          <div
                            className="mobile-fb-review-info"
                            onClick={() => handleDetailView(item.nttSn)}
                          >
                            <span
                              className="mobile-fb-author"
                              title={item.nttSj}
                              onClick={() => handleDetailView(item.nttSn)}
                            >
                              {item.nttSj}
                              {item.answerCnt ? (
                                <span className="feedback-answer-count">
                                  [{item.answerCnt}]
                                </span>
                              ) : null}
                            </span>
                            <span
                              className="mobile-fb-stats"
                              title={item.nickName}
                              onClick={() => handleDetailView(item.nttSn)}
                            >
                              {`작성자 : ${item.nickName} | 작성일 : ${formatDate(item.regDate, true)}`}
                            </span>
                          </div>
                          <button className="mobile-fb-like-btn">
                            <LikeButton
                              likesYn={item.likesYn}
                              likeCnt={item.likeCnt}
                              nttSn={item.nttSn}
                              fdbkType={item.fdbkType}
                              isMobile={isMobile}
                            />
                          </button>
                        </div>
                      ))}
                    </div>
                  </section>
                </>
              )}
              {activeTab === 'bad' && (
                <section
                  className={`mobile-fb-reviews-section bad-reviews ${activeTab === 'bad' ? 'active' : ''}`}
                  id="bad-reviews"
                  style={{ margin: '0px', padding: '0px' }}
                >
                  <div className="mobile-fb-reviews-grid">
                    {suggestionList.slice(0, 3).map((item, index) => (
                      <div className="mobile-fb-review-card" key={item.nttSn}>
                        <h3
                          className="mobile-fb-book-title"
                          onClick={() => handleDetailView(item.nttSn)}
                        >
                          {smartLineBreak(item.bizNm)}
                        </h3>
                        <div
                          className="mobile-fb-review-meta"
                          onClick={() => handleDetailView(item.nttSn)}
                        >
                          <span
                            className="mobile-fb-review-date"
                            onClick={() => handleDetailView(item.nttSn)}
                          >
                            {`라운드날짜 : ${formatDateString(item.rndDt)}`}
                          </span>
                        </div>
                        <div
                          className="mobile-fb-review-info"
                          onClick={() => handleDetailView(item.nttSn)}
                        >
                          <span
                            className="mobile-fb-author"
                            title={item.nttSj}
                            onClick={() => handleDetailView(item.nttSn)}
                          >
                            {item.nttSj}
                            {item.answerCnt ? (
                              <span className="feedback-answer-count">
                                [{item.answerCnt}]
                              </span>
                            ) : null}
                          </span>
                          <span
                            className="mobile-fb-stats"
                            title={item.nickName}
                            onClick={() => handleDetailView(item.nttSn)}
                          >
                            {`작성자 : ${item.nickName} | 작성일 : ${formatDate(item.regDate, true)}`}
                          </span>
                        </div>
                        <button className="mobile-fb-like-btn">
                          <LikeButton
                            likesYn={item.likesYn}
                            likeCnt={item.likeCnt}
                            nttSn={item.nttSn}
                            fdbkType={item.fdbkType}
                            isMobile={isMobile}
                          />
                        </button>
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </div>
          </>
        )}
      </MainFeedbackWrapper>
    </>
  )
}

export const MainFeedbackWrapper = styled.div`
  border: 1px solid #ddd;
  padding: 12px 15px 12px 12px;
  border-radius: 8px;
  width: 100%;
  border-bottom: 1px solid #ddd; /* 테두리 유지 */
  box-sizing: border-box; /* padding이 border에 포함되도록 설정 */
  flex: 1; /* 남는 높이 없게끔 가져가기 */
`
